import React, { useMemo, useState } from 'react';

const DEFAULT_COLORS = {
  bg: '#07151b',
  card: '#10242d',
  card2: '#132c36',
  border: '#234451',
  text: '#e7f3f6',
  muted: '#91aebb',
  cyan: '#22d3ee',
  green: '#22c55e',
  yellow: '#f59e0b',
  red: '#ef4444',
  teal: '#14b8a6',
  input: '#dbe9ef',
  inputText: '#10202a',
};

const OBJECTIVES = [
  {
    key: 'reach',
    label: 'Reichweite',
    focus: 'ein klares Problem schnell sichtbar machen',
    cta: 'Folge Einfach-Trading, wenn du regelbasierter investieren willst.',
  },
  {
    key: 'trust',
    label: 'Vertrauen',
    focus: 'zeigen, wie transparent und nachvollziehbar die App arbeitet',
    cta: 'Teste die App und prüfe die Signale in Ruhe selbst.',
  },
  {
    key: 'educate',
    label: 'App erklären',
    focus: 'eine Funktion so erklären, dass sie sofort nutzbar wird',
    cta: 'Öffne die App und probiere den Ablauf mit deinem Index aus.',
  },
  {
    key: 'convert',
    label: 'Testaccount',
    focus: 'den konkreten Nutzen vor dem Klick auf den Testaccount zeigen',
    cta: 'Starte deinen kostenlosen Testzeitraum auf einfach-trading.de.',
  },
  {
    key: 'community',
    label: 'Community-Frage',
    focus: 'eine echte Nutzerfrage beantworten und Diskussion auslösen',
    cta: 'Schreib deine Frage in die Kommentare oder in die Community.',
  },
  {
    key: 'youtube',
    label: 'YouTube-Video',
    focus: 'ein Thema mit rotem Faden und Kapitelstruktur aufbauen',
    cta: 'Abonniere den Kanal, wenn du die App Schritt für Schritt verstehen willst.',
  },
];

const AUDIENCES = [
  {
    key: 'beginner',
    label: 'Einsteiger',
    angle: 'einfach, ohne Fachjargon, mit einem konkreten Beispiel',
  },
  {
    key: 'busy',
    label: 'Berufstätige Anleger',
    angle: 'zeitsparend, klarer Wochenablauf, keine tägliche Markt-Hektik',
  },
  {
    key: 'systematic',
    label: 'Regelbasierte Anleger',
    angle: 'Regeln, Kennzahlen, Wiederholbarkeit und Disziplin betonen',
  },
  {
    key: 'etf',
    label: 'ETF-Anleger',
    angle: 'Index-Invest, Drawdown, Cash-Phasen und langfristige Steuerung erklären',
  },
  {
    key: 'community',
    label: 'Community',
    angle: 'direkt auf typische Fragen und Missverständnisse eingehen',
  },
];

const PILLARS = [
  {
    key: 'rsl_basics',
    label: 'RSL-Grundlagen',
    promise: 'Relative Stärke nach Levy verständlich machen.',
  },
  {
    key: 'app_features',
    label: 'App-Funktionen',
    promise: 'Scanner, Depot, Journal, Backtest und Benachrichtigungen erklären.',
  },
  {
    key: 'workflow',
    label: 'Workflows',
    promise: 'Zeigen, wie Nutzer die App im Alltag konkret einsetzen.',
  },
  {
    key: 'backtest',
    label: 'Backtest & Daten',
    promise: 'Ergebnisse einordnen, ohne falsche Sicherheit zu versprechen.',
  },
  {
    key: 'index_invest',
    label: 'Index-Invest',
    promise: 'ETF-Ampel, Status und Cash-Phasen erklären.',
  },
  {
    key: 'mistakes',
    label: 'Fehler vermeiden',
    promise: 'Typische Denkfehler beim regelbasierten Investieren entschärfen.',
  },
  {
    key: 'community_questions',
    label: 'Community-Fragen',
    promise: 'Echte Fragen aus Support, YouTube, Instagram und Telegram beantworten.',
  },
];

const FORMATS = [
  {
    key: 'reel',
    label: 'Instagram Reel',
    length: '20-45 Sekunden',
    structure: 'Hook, Problem, App-Beispiel, Erkenntnis, CTA',
  },
  {
    key: 'carousel',
    label: 'Instagram Carousel',
    length: '6-8 Slides',
    structure: 'These, Erklärung, Beispiel, Fehler, App-Bezug, CTA',
  },
  {
    key: 'story',
    label: 'Story',
    length: '3-5 Frames',
    structure: 'Frage, Mini-Antwort, Abstimmung oder Link',
  },
  {
    key: 'youtube_short',
    label: 'YouTube Short',
    length: '30-60 Sekunden',
    structure: 'starker Einstieg, eine Idee, klares Ende',
  },
  {
    key: 'youtube_long',
    label: 'YouTube Longform',
    length: '6-12 Minuten',
    structure: 'Versprechen, Grundlagen, Demo, Einordnung, Fazit',
  },
  {
    key: 'telegram',
    label: 'Telegram-Post',
    length: 'kurz und direkt',
    structure: 'Marktbezug, Nutzen, Link oder Frage',
  },
];

const HOOK_TYPES = [
  {
    key: 'mistake',
    label: 'Fehler',
    prefix: 'Viele Anleger machen genau hier den Fehler:',
  },
  {
    key: 'question',
    label: 'Frage',
    prefix: 'Eine Frage, die wir oft bekommen:',
  },
  {
    key: 'myth',
    label: 'Mythos',
    prefix: 'Mythos:',
  },
  {
    key: 'proof',
    label: 'Beleg',
    prefix: 'Der spannende Punkt aus den Daten:',
  },
  {
    key: 'workflow',
    label: 'Workflow',
    prefix: 'So würde ich es in der App prüfen:',
  },
];

const TOPICS = {
  rsl_basics: [
    {
      key: 'rsl_not_rsi',
      title: 'RSL ist nicht RSI',
      problem: 'Viele verwechseln Relative Stärke nach Levy mit dem kurzfristigen RSI-Indikator.',
      insight: 'RSL bewertet die längerfristige Trendstärke einer Aktie gegenüber ihrem Durchschnitt.',
      appExample: 'Im Scanner werden Titel nach ihrem RSL-Wert sortiert, damit starke Trends sichtbar werden.',
    },
    {
      key: 'rsl_over_one',
      title: 'Was bedeutet RSL über 1?',
      problem: 'Ein RSL-Wert wirkt abstrakt, wenn man ihn nicht in Kurs und Durchschnitt übersetzt.',
      insight: 'Über 1 bedeutet: Der Kurs liegt oberhalb seines langfristigen Durchschnitts.',
      appExample: 'Die App zeigt RSL-Wert und Rang, damit Stärke nicht nur gefühlt, sondern messbar wird.',
    },
    {
      key: 'strength_persists',
      title: 'Warum starke Aktien oft stark bleiben',
      problem: 'Viele verkaufen Gewinner zu früh und halten schwache Titel zu lange.',
      insight: 'Trendfolge versucht, bestehende Stärke systematisch zu nutzen, statt Wendepunkte zu erraten.',
      appExample: 'Das Depot-Signal hilft, starke Titel zu halten und schwächer werdende Titel zu ersetzen.',
    },
  ],
  app_features: [
    {
      key: 'scanner',
      title: 'RSL-Scanner',
      problem: 'Ohne System verliert man schnell den Überblick über hunderte Aktien.',
      insight: 'Der Scanner reduziert die Auswahl auf die stärksten Titel je Index.',
      appExample: 'S&P 500, Nasdaq 100, HDAX und STOXX 600 lassen sich getrennt prüfen.',
    },
    {
      key: 'depot_signals',
      title: 'Depot-Signale',
      problem: 'Kaufen ist einfach, aber das konsequente Austauschen fällt schwer.',
      insight: 'Halten, Beobachten und Austauschen machen den nächsten Schritt klarer.',
      appExample: 'Im RSL-Depot wird pro Position angezeigt, ob der Trend intakt ist.',
    },
    {
      key: 'analysis_mode',
      title: 'Analyse-Modus',
      problem: 'Ein Signal allein erklärt noch nicht, wie nah eine Aktie am Ausstieg ist.',
      insight: 'Der Analyse-Modus zeigt, wie weit ein Titel gelaufen ist und wie viel Puffer er aktuell hat.',
      appExample: 'So kann man erkennen, ob ein Titel stabil wirkt oder bereits nahe an der Regelgrenze liegt.',
    },
    {
      key: 'rsl_journal',
      title: 'RSL-Journal',
      problem: 'Ohne Dokumentation weiß man später nicht mehr, warum man gehandelt hat.',
      insight: 'Ein Journal macht Entscheidungen nachvollziehbar und hilft, Regeln sauber einzuhalten.',
      appExample: 'Im RSL-Journal lassen sich Käufe, Verkäufe und Gedanken zur Entscheidung festhalten.',
    },
  ],
  workflow: [
    {
      key: 'monday_check',
      title: '10-Minuten-Montagscheck',
      problem: 'Viele Anleger prüfen ihr Depot zu oft und reagieren auf jedes Marktrauschen.',
      insight: 'Ein fester Wochenrhythmus reduziert Aktionismus und stärkt die Regelbindung.',
      appExample: 'Scanner öffnen, Depot-Signale prüfen, mögliche Austausche ansehen, Entscheidung dokumentieren.',
    },
    {
      key: 'first_login',
      title: 'Die ersten 5 Minuten in der App',
      problem: 'Neue Nutzer wissen oft nicht, wo sie anfangen sollen.',
      insight: 'Erst Index wählen, dann Scanner verstehen, danach Depot und Anleitung nutzen.',
      appExample: 'Startseite, Anleitung, Scanner und Depot bilden zusammen den einfachen Einstieg.',
    },
    {
      key: 'signal_to_action',
      title: 'Vom Signal zur Entscheidung',
      problem: 'Ein Signal ersetzt keine Verantwortung, aber es gibt eine klare Struktur.',
      insight: 'Die App liefert Orientierung, die Entscheidung bleibt beim Nutzer.',
      appExample: 'Signal prüfen, Chart ansehen, Kostenbasis kennen, Handlung dokumentieren.',
    },
  ],
  backtest: [
    {
      key: 'read_backtest',
      title: 'Backtests richtig lesen',
      problem: 'Der höchste Endwert ist nicht automatisch die beste Strategie.',
      insight: 'Stabilität, Drawdown, ROR und Vergleichbarkeit sind wichtiger als ein einzelner Spitzenwert.',
      appExample: 'Der Parameter-Vergleich zeigt mehrere Varianten nebeneinander.',
    },
    {
      key: 'benchmark',
      title: 'Benchmark vs Strategie',
      problem: 'Ohne Vergleich zum Index weiß man nicht, ob eine Strategie wirklich Mehrwert bringt.',
      insight: 'Eine Strategie muss nicht nur steigen, sie sollte ihren Vergleichsmaßstab sinnvoll schlagen.',
      appExample: 'Die App stellt Strategie und Benchmark im Backtest gegenüber.',
    },
    {
      key: 'history_limits',
      title: 'Was historische Daten nicht versprechen',
      problem: 'Backtests können zu viel Sicherheit erzeugen.',
      insight: 'Vergangenheit ist ein Testfeld, aber keine Garantie für die Zukunft.',
      appExample: 'Darum zeigt die App Hinweise zur Datenbasis und arbeitet regelbasiert statt prognosebasiert.',
    },
  ],
  index_invest: [
    {
      key: 'traffic_light',
      title: 'Grün, Gelb, Rot bei Index-Invest',
      problem: 'ETF-Anleger fragen oft, ob sie gerade investiert sein sollten oder lieber Cash halten.',
      insight: 'Die Ampel übersetzt den Trendstatus in einen einfachen Regelzustand.',
      appExample: 'Grün bedeutet investiert, Rot bedeutet Cash, Gelb markiert einen frischen Wechsel.',
    },
    {
      key: 'cash_phase',
      title: 'Warum Cash-Phasen kein Fehler sein müssen',
      problem: 'Viele fürchten, beim Ausstieg den Zinseszinseffekt zu verpassen.',
      insight: 'Die Idee ist nicht dauerndes Rein und Raus, sondern große Schwächephasen zu reduzieren.',
      appExample: 'Index-Invest zeigt den Verlauf von Benchmark, Strategie und 2x-Variante.',
    },
    {
      key: 'weekly_signal',
      title: 'Warum Index-Invest wöchentlich arbeitet',
      problem: 'Tägliche Signale können hektisch wirken und zu vielen Wechseln führen.',
      insight: 'Ein Wochenrhythmus filtert Rauschen und passt besser zu langfristigen ETF-Entscheidungen.',
      appExample: 'Die E-Mail-Benachrichtigung kann den Status in die wöchentliche Mail aufnehmen.',
    },
  ],
  mistakes: [
    {
      key: 'blind_top5',
      title: 'Top 5 nicht blind kaufen',
      problem: 'Eine Rangliste ist eine Vorauswahl, aber kein Freifahrtschein.',
      insight: 'Index, Positionsanzahl, Regelwerk und persönliche Risikogröße müssen zusammenpassen.',
      appExample: 'Die App zeigt Rang, Signal, RSL, Kostenbasis und Marktwert im Kontext.',
    },
    {
      key: 'changing_rules',
      title: 'Regeln nicht ständig ändern',
      problem: 'Wer nach jedem schlechten Trade die Regeln ändert, testet nie dieselbe Strategie.',
      insight: 'Ein System braucht Konstanz, sonst wird aus Strategie wieder Bauchgefühl.',
      appExample: 'Admin-Strategien und Backtests helfen, Parameter bewusst statt spontan zu wählen.',
    },
    {
      key: 'performance_chasing',
      title: 'Nicht nur auf den Endwert schauen',
      problem: 'Ein hoher Endwert kann mit extremen Schwankungen erkauft sein.',
      insight: 'Drawdown, Trades, ROR und Stabilität gehören immer dazu.',
      appExample: 'Der Parameter-Vergleich macht solche Unterschiede sichtbar.',
    },
  ],
  community_questions: [
    {
      key: 'one_or_many_indices',
      title: 'Ein Index oder mehrere?',
      problem: 'Nutzer fragen, ob sie aus jedem Index einen Top-Wert nehmen sollten.',
      insight: 'Die Tests sind je Index gedacht; gemischte Zusammenstellungen müssen separat betrachtet werden.',
      appExample: 'Darum zeigt die App S&P 500, Nasdaq 100, HDAX und STOXX 600 getrennt.',
    },
    {
      key: 'tradingview_rsl',
      title: 'TradingView RSL richtig einordnen',
      problem: 'Community-Indikatoren können ähnlich heißen, aber anders berechnet sein.',
      insight: 'Wichtig ist, welche Formel, Datenbasis und Signallogik verwendet wird.',
      appExample: 'Einfach-Trading nutzt die App-eigene RSL-Logik und zeigt die Signale direkt im Depot.',
    },
    {
      key: 'yellow_status',
      title: 'Wie lange bleibt Index-Invest Gelb?',
      problem: 'Gelb kann missverstanden werden, wenn man nicht weiß, dass es einen frischen Wechsel markiert.',
      insight: 'Gelb ist kein eigener Dauerzustand, sondern ein Hinweis auf den Wechselzeitpunkt.',
      appExample: 'Nach der Wechselwoche wird der Status wieder als Grün oder Rot dargestellt.',
    },
  ],
};

const SERIES = [
  {
    title: 'RSL in 60 Sekunden',
    cadence: '1x pro Woche',
    pillar: 'RSL-Grundlagen',
    format: 'Reel oder Short',
    idea: 'Ein Begriff, ein Beispiel, eine klare Erkenntnis.',
  },
  {
    title: 'App-Funktion der Woche',
    cadence: '1x pro Woche',
    pillar: 'App-Funktionen',
    format: 'Carousel',
    idea: 'Eine Funktion mit Screenshot-Logik und Mini-Workflow erklären.',
  },
  {
    title: 'Montags-Check',
    cadence: 'montags',
    pillar: 'Workflow',
    format: 'Story oder Telegram',
    idea: 'Was wird diese Woche geprüft, ohne konkrete Kaufempfehlung.',
  },
  {
    title: 'Backtest richtig lesen',
    cadence: 'alle 2 Wochen',
    pillar: 'Backtest & Daten',
    format: 'YouTube oder Carousel',
    idea: 'Eine Kennzahl erklären und Fehlinterpretationen vermeiden.',
  },
  {
    title: 'Community-Frage',
    cadence: 'nach Bedarf',
    pillar: 'Community',
    format: 'Reel, Short oder Telegram',
    idea: 'Eine echte Frage kurz, ehrlich und wiederverwendbar beantworten.',
  },
  {
    title: 'Index-Invest Ampel',
    cadence: 'bei Wechsel oder Erklärthema',
    pillar: 'Index-Invest',
    format: 'Short oder Carousel',
    idea: 'Grün, Gelb, Rot und Cash-Logik verständlich machen.',
  },
];

const QUALITY_RULES = [
  'Ein Beitrag beantwortet nur eine zentrale Frage.',
  'Der Hook muss in den ersten 2 Sekunden verständlich sein.',
  'Der App-Bezug zeigt einen konkreten Nutzen, keine Feature-Liste.',
  'Jede Aussage braucht Kontext: Index, Zeitraum, Regel oder Beispiel.',
  'Keine Renditeversprechen, keine Kaufaufforderung, keine Scheinsicherheit.',
  'Am Ende steht ein klarer nächster Schritt: testen, kommentieren, ansehen oder speichern.',
];

function byKey(items, key) {
  return items.find((item) => item.key === key) || items[0];
}

function buildHook(hookType, topic, audience) {
  const hook = byKey(HOOK_TYPES, hookType);
  if (hookType === 'question') {
    return `${hook.prefix} ${topic.title} - was bedeutet das praktisch für dein Depot?`;
  }
  if (hookType === 'myth') {
    return `${hook.prefix} "${topic.title} ist nur etwas für Profis." Stimmt so nicht.`;
  }
  if (hookType === 'proof') {
    return `${hook.prefix} ${topic.title} wird erst spannend, wenn man es mit Regeln statt Bauchgefühl verbindet.`;
  }
  if (hookType === 'workflow') {
    return `${hook.prefix} ${topic.title} in der App prüfen - ohne jeden Tag den Markt zu jagen.`;
  }
  return `${hook.prefix} ${topic.problem}`;
}

function buildScript(formatKey, topic, objective, audience) {
  if (formatKey === 'youtube_long') {
    return [
      `0:00 Hook: ${topic.problem}`,
      `0:30 Einordnung: Warum das Thema für ${audience.label} relevant ist.`,
      `1:30 Theorie: ${topic.insight}`,
      `3:00 Praxis in der App: ${topic.appExample}`,
      '5:00 Typischer Fehler und wie man ihn vermeidet.',
      '7:00 Was die App zeigt und was sie bewusst nicht verspricht.',
      '9:00 Zusammenfassung in drei Punkten.',
      `10:00 CTA: ${objective.cta}`,
    ];
  }

  if (formatKey === 'carousel') {
    return [
      `Slide 1: ${topic.title}`,
      `Slide 2: Das Problem - ${topic.problem}`,
      `Slide 3: Die Regel - ${topic.insight}`,
      `Slide 4: App-Beispiel - ${topic.appExample}`,
      'Slide 5: Häufiger Fehler - zu viele Dinge gleichzeitig interpretieren.',
      'Slide 6: Merksatz - erst Regel verstehen, dann handeln.',
      `Slide 7: CTA - ${objective.cta}`,
    ];
  }

  if (formatKey === 'story') {
    return [
      `Frame 1: Frage: ${topic.title}?`,
      `Frame 2: Kurzantwort: ${topic.insight}`,
      `Frame 3: App-Bezug: ${topic.appExample}`,
      'Frame 4: Umfrage: Nutzt du dafür schon eine feste Regel?',
    ];
  }

  if (formatKey === 'telegram') {
    return [
      `Kurzthese: ${topic.title}`,
      `Warum wichtig: ${topic.problem}`,
      `Praxis: ${topic.appExample}`,
      `Nächster Schritt: ${objective.cta}`,
    ];
  }

  return [
    `Hook: ${topic.problem}`,
    `Kontext: Für ${audience.label} ist wichtig: ${audience.angle}.`,
    `Kernidee: ${topic.insight}`,
    `App-Beispiel: ${topic.appExample}`,
    `CTA: ${objective.cta}`,
  ];
}

function buildCaption(topic, objective, audience) {
  return [
    `${topic.title}: ${topic.insight}`,
    '',
    `In der Praxis geht es nicht darum, den perfekten Einstieg vorherzusagen. Es geht darum, Entscheidungen nachvollziehbarer zu machen und nicht jedes Mal aus dem Bauch heraus zu handeln.`,
    '',
    `Für ${audience.label}: ${audience.angle}.`,
    '',
    `In Einfach-Trading wird daraus ein konkreter Workflow: ${topic.appExample}`,
    '',
    objective.cta,
    '',
    'Hinweis: Keine Anlageberatung. Historische Daten und Signale sind keine Garantie für zukünftige Ergebnisse.',
  ].join('\n');
}

function buildTitles(topic, objective) {
  return [
    `${topic.title}: einfach erklärt`,
    `Der häufige Fehler bei ${topic.title}`,
    `So nutzt du ${topic.title} in Einfach-Trading`,
    `Warum ${topic.title} für regelbasierte Anleger wichtig ist`,
    `${objective.label}: ${topic.title} als Content-Idee`,
  ];
}

function buildThumbnailIdeas(topic) {
  return [
    `Großer Text: "${topic.title}" + App-Screenshot im Hintergrund`,
    `Vorher/Nachher: Bauchgefühl vs. Regel`,
    `Ein klares Symbol: Rangliste, Ampel oder Signal-Badge`,
  ];
}

function buildPackageText(content) {
  return [
    `HOOK\n${content.hook}`,
    `ABLAUF\n${content.script.join('\n')}`,
    `CAPTION\n${content.caption}`,
    `TITEL\n${content.titles.join('\n')}`,
  ].join('\n\n');
}

function chipStyle(C, active = false) {
  return {
    border: `1px solid ${active ? C.cyan : C.border}`,
    background: active ? C.cyan : C.card2,
    color: active ? '#06131a' : C.text,
    borderRadius: 9,
    padding: '8px 11px',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
  };
}

function fieldStyle(C) {
  return {
    width: '100%',
    padding: '11px 12px',
    borderRadius: 9,
    border: `1px solid ${C.border}`,
    background: C.input,
    color: C.inputText,
    fontWeight: 700,
    outline: 'none',
  };
}

function labelStyle(C) {
  return {
    display: 'block',
    color: C.muted,
    fontSize: 12,
    fontWeight: 800,
    marginBottom: 7,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  };
}

function Panel({ C, title, action, children }) {
  return (
    <section style={{
      background: C.card,
      border: `1px solid ${C.border}`,
      borderRadius: 14,
      padding: 20,
      boxShadow: '0 16px 40px rgba(0,0,0,0.18)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ color: C.text, fontSize: 18, margin: 0 }}>{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function CopyButton({ C, onClick, children = 'Kopieren' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: C.green,
        color: '#06200f',
        border: 'none',
        borderRadius: 9,
        padding: '8px 12px',
        fontWeight: 900,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

function TextBlock({ C, children }) {
  return (
    <pre style={{
      margin: 0,
      whiteSpace: 'pre-wrap',
      fontFamily: 'inherit',
      color: C.text,
      lineHeight: 1.55,
      fontSize: 14,
    }}>
      {children}
    </pre>
  );
}

function InfoCard({ C, title, value, detail, accent }) {
  return (
    <div style={{
      border: `1px solid ${C.border}`,
      background: C.card2,
      borderRadius: 12,
      padding: 14,
      minHeight: 112,
    }}>
      <div style={{ color: accent || C.cyan, fontSize: 12, fontWeight: 900, textTransform: 'uppercase', marginBottom: 8 }}>
        {title}
      </div>
      <div style={{ color: C.text, fontSize: 20, fontWeight: 900, marginBottom: 8 }}>
        {value}
      </div>
      <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.45 }}>
        {detail}
      </div>
    </div>
  );
}

export default function ContentStudioSection({ COLORS, onToast }) {
  const C = { ...DEFAULT_COLORS, ...(COLORS || {}) };
  const [objectiveKey, setObjectiveKey] = useState('educate');
  const [audienceKey, setAudienceKey] = useState('systematic');
  const [pillarKey, setPillarKey] = useState('app_features');
  const [topicKey, setTopicKey] = useState('scanner');
  const [formatKey, setFormatKey] = useState('carousel');
  const [hookType, setHookType] = useState('question');

  const topicOptions = TOPICS[pillarKey] || TOPICS.rsl_basics;
  const objective = byKey(OBJECTIVES, objectiveKey);
  const audience = byKey(AUDIENCES, audienceKey);
  const pillar = byKey(PILLARS, pillarKey);
  const format = byKey(FORMATS, formatKey);
  const topic = topicOptions.find((item) => item.key === topicKey) || topicOptions[0];

  const content = useMemo(() => {
    const next = {
      hook: buildHook(hookType, topic, audience),
      script: buildScript(formatKey, topic, objective, audience),
      caption: buildCaption(topic, objective, audience),
      titles: buildTitles(topic, objective),
      thumbnails: buildThumbnailIdeas(topic),
    };
    next.package = buildPackageText(next);
    return next;
  }, [hookType, topic, audience, formatKey, objective]);

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      if (onToast) onToast(`${label} kopiert.`, 'success');
    } catch (error) {
      if (onToast) onToast('Kopieren nicht möglich.', 'error');
    }
  };

  const chooseSeries = (series) => {
    const pillarMatch = PILLARS.find((item) => item.label === series.pillar || series.pillar.includes(item.label.split(' ')[0]));
    if (pillarMatch) {
      setPillarKey(pillarMatch.key);
      const firstTopic = TOPICS[pillarMatch.key]?.[0];
      if (firstTopic) setTopicKey(firstTopic.key);
    }
    if (series.format.includes('Carousel')) setFormatKey('carousel');
    else if (series.format.includes('Short')) setFormatKey('youtube_short');
    else if (series.format.includes('Story')) setFormatKey('story');
    else setFormatKey('reel');
  };

  return (
    <div style={{ color: C.text }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 18,
        marginBottom: 20,
      }}>
        <div>
          <h1 style={{ margin: 0, color: C.text, fontSize: 30 }}>Contentstudio</h1>
          <p style={{ margin: '8px 0 0', color: C.muted, maxWidth: 900, lineHeight: 1.55 }}>
            Redaktionsassistent für Einfach-Trading: Ziel, Zielgruppe, Thema und Format wählen.
            Das Studio erzeugt daraus Hook, Ablauf, Caption, Titelideen und eine Qualitätsprüfung.
          </p>
        </div>
        <CopyButton C={C} onClick={() => copyText(content.package, 'Content-Paket')}>Alles kopieren</CopyButton>
      </div>

      <Panel C={C} title="Content-Briefing">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 14,
        }}>
          <div>
            <label style={labelStyle(C)}>Ziel</label>
            <select style={fieldStyle(C)} value={objectiveKey} onChange={(event) => setObjectiveKey(event.target.value)}>
              {OBJECTIVES.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle(C)}>Zielgruppe</label>
            <select style={fieldStyle(C)} value={audienceKey} onChange={(event) => setAudienceKey(event.target.value)}>
              {AUDIENCES.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle(C)}>Format</label>
            <select style={fieldStyle(C)} value={formatKey} onChange={(event) => setFormatKey(event.target.value)}>
              {FORMATS.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle(C)}>Content-Säule</label>
            <select
              style={fieldStyle(C)}
              value={pillarKey}
              onChange={(event) => {
                const nextPillar = event.target.value;
                setPillarKey(nextPillar);
                setTopicKey((TOPICS[nextPillar] || TOPICS.rsl_basics)[0].key);
              }}
            >
              {PILLARS.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle(C)}>Thema</label>
            <select style={fieldStyle(C)} value={topic.key} onChange={(event) => setTopicKey(event.target.value)}>
              {topicOptions.map((item) => <option key={item.key} value={item.key}>{item.title}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle(C)}>Hook-Typ</label>
            <select style={fieldStyle(C)} value={hookType} onChange={(event) => setHookType(event.target.value)}>
              {HOOK_TYPES.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14, marginTop: 16 }}>
          <InfoCard C={C} title="Ziel-Fokus" value={objective.label} detail={objective.focus} accent={C.green} />
          <InfoCard C={C} title="Content-Säule" value={pillar.label} detail={pillar.promise} accent={C.cyan} />
          <InfoCard C={C} title="Format-Logik" value={format.length} detail={format.structure} accent={C.yellow} />
        </div>
      </Panel>

      <div style={{ height: 18 }} />

      <Panel C={C} title="Serien-Ideen">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 12,
        }}>
          {SERIES.map((series) => (
            <button
              type="button"
              key={series.title}
              onClick={() => chooseSeries(series)}
              style={{
                textAlign: 'left',
                background: C.card2,
                border: `1px solid ${C.border}`,
                borderRadius: 12,
                padding: 14,
                cursor: 'pointer',
                color: C.text,
              }}
            >
              <div style={{ color: C.cyan, fontWeight: 900, marginBottom: 6 }}>{series.title}</div>
              <div style={{ color: C.muted, fontSize: 12, marginBottom: 8 }}>{series.cadence} · {series.format}</div>
              <div style={{ fontSize: 13, lineHeight: 1.45 }}>{series.idea}</div>
            </button>
          ))}
        </div>
      </Panel>

      <div style={{ height: 18 }} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        <Panel
          C={C}
          title="Hook"
          action={<CopyButton C={C} onClick={() => copyText(content.hook, 'Hook')} />}
        >
          <TextBlock C={C}>{content.hook}</TextBlock>
        </Panel>

        <Panel
          C={C}
          title="Titel & Thumbnail"
          action={<CopyButton C={C} onClick={() => copyText([...content.titles, '', ...content.thumbnails].join('\n'), 'Titelideen')} />}
        >
          <TextBlock C={C}>{[
            ...content.titles.map((item) => `- ${item}`),
            '',
            'Thumbnail:',
            ...content.thumbnails.map((item) => `- ${item}`),
          ].join('\n')}</TextBlock>
        </Panel>
      </div>

      <div style={{ height: 18 }} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        <Panel
          C={C}
          title="Skript / Ablauf"
          action={<CopyButton C={C} onClick={() => copyText(content.script.join('\n'), 'Ablauf')} />}
        >
          <TextBlock C={C}>{content.script.map((line) => `- ${line}`).join('\n')}</TextBlock>
        </Panel>

        <Panel
          C={C}
          title="Caption"
          action={<CopyButton C={C} onClick={() => copyText(content.caption, 'Caption')} />}
        >
          <TextBlock C={C}>{content.caption}</TextBlock>
        </Panel>
      </div>

      <div style={{ height: 18 }} />

      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 18 }}>
        <Panel C={C} title="Redaktionsprüfung">
          <div style={{ display: 'grid', gap: 9 }}>
            {QUALITY_RULES.map((rule, index) => (
              <div key={rule} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', color: C.text }}>
                <span style={{
                  width: 22,
                  height: 22,
                  borderRadius: 999,
                  background: C.card2,
                  border: `1px solid ${C.border}`,
                  color: C.cyan,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 900,
                  flex: '0 0 auto',
                }}>
                  {index + 1}
                </span>
                <span style={{ color: C.muted, lineHeight: 1.45 }}>{rule}</span>
              </div>
            ))}
          </div>
        </Panel>

        <Panel C={C} title="Schnellwahl">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {PILLARS.map((item) => (
              <button
                type="button"
                key={item.key}
                onClick={() => {
                  setPillarKey(item.key);
                  setTopicKey((TOPICS[item.key] || TOPICS.rsl_basics)[0].key);
                }}
                style={chipStyle(C, item.key === pillarKey)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div style={{ height: 14 }} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {FORMATS.map((item) => (
              <button
                type="button"
                key={item.key}
                onClick={() => setFormatKey(item.key)}
                style={chipStyle(C, item.key === formatKey)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
